import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ChevronRight, Play, Film, Tv, Award, Popcorn, Sparkles, Clapperboard,
  Globe, Crown, ArrowRight, Check, Star, Users, Radio, X,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

const AB_HEADLINES = {
  A: { lines: ["One ticket.", <><span className="text-grad-red">Four</span> ways to</>, "experience cinema."], full: "One ticket. Four ways to experience cinema." },
  B: { lines: ["The cinema", <>you <span className="text-grad-red">carry.</span></>, "Wherever you sit."], full: "The cinema you carry. Wherever you sit." },
};

// Tasteful royalty-free cinema/projector loop. Operators can override via
// backend env `LANDING_HERO_VIDEO_URL` (returned in /api/landing/stats).
const DEFAULT_SIZZLE_REEL = "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerJoyrides.mp4";

function pickVariant() {
  try {
    let v = localStorage.getItem("cc_ab_variant");
    if (v === "A" || v === "B") return v;
    v = Math.random() < 0.5 ? "A" : "B";
    localStorage.setItem("cc_ab_variant", v);
    return v;
  } catch { return "A"; }
}
function getVisitorId() {
  try {
    let id = localStorage.getItem("cc_visitor_id");
    if (!id) {
      id = "v_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
      localStorage.setItem("cc_visitor_id", id);
    }
    return id;
  } catch { return "anon"; }
}

export default function Landing() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [featured, setFeatured] = useState({ movies: [], festival_films: [] });
  const [variant] = useState(pickVariant);
  const [showTrailer, setShowTrailer] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const impressionLoggedRef = useRef(false);
  const reducedMotion = useMemo(
    () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
    [],
  );

  // Already authenticated → straight to browse
  useEffect(() => {
    if (user?.user_id) nav("/browse", { replace: true });
  }, [user, nav]);

  useEffect(() => {
    api.get("/landing/stats").then((r) => setStats(r.data)).catch(() => setStats(null));
    api.get("/landing/featured").then((r) => setFeatured(r.data || { movies: [], festival_films: [] })).catch(() => {});
  }, []);

  // Log A/B impression once per page-load
  useEffect(() => {
    if (impressionLoggedRef.current) return;
    impressionLoggedRef.current = true;
    api.post("/landing/track", {
      variant,
      event: "impression",
      visitor_id: getVisitorId(),
      headline: AB_HEADLINES[variant].full,
    }).catch(() => {});
  }, [variant]);

  // Parallax scroll listener (skip if user prefers reduced motion)
  useEffect(() => {
    if (reducedMotion) return;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setScrollY(window.scrollY);
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [reducedMotion]);

  const onSignupClick = () => {
    api.post("/landing/track", {
      variant,
      event: "signup_click",
      visitor_id: getVisitorId(),
      headline: AB_HEADLINES[variant].full,
    }).catch(() => {});
    try {
      localStorage.setItem("cc_ab_pending_signup", JSON.stringify({ variant, t: Date.now() }));
    } catch (_e) { /* ignore */ }
    nav("/signup");
  };

  const heroBackdrop = useMemo(() => {
    const b = featured.movies?.find((m) => m.backdrop)?.backdrop;
    if (b) return b.startsWith("http") ? b : `${process.env.REACT_APP_BACKEND_URL || ""}${b}`;
    return "https://images.unsplash.com/photo-1542204165-65bf26472b9b?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400";
  }, [featured]);

  const sizzleUrl = stats?.hero_video_url || DEFAULT_SIZZLE_REEL;
  const headline = AB_HEADLINES[variant];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden">
      {/* ============ HERO ============ */}
      <section className="relative min-h-[100svh] flex flex-col">
        {/* Sizzle reel (muted, looped, autoplay) layered behind the backdrop image */}
        <video
          data-testid="hero-sizzle-reel"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
          src={sizzleUrl}
          poster={heroBackdrop}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
        />
        <img src={heroBackdrop} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25 mix-blend-multiply" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/55 via-[#0A0A0A]/40 to-[#0A0A0A]" />
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.05] mix-blend-overlay"
          style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22100%22 height=%22100%22%3E%3Cfilter id=%22n%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22/%3E%3C/filter%3E%3Crect width=%22100%22 height=%22100%22 filter=%22url(%23n)%22/%3E%3C/svg%3E")' }}
        />

        {/* Header */}
        <header className="relative z-20 flex items-center justify-between px-6 lg:px-12 py-6">
          <div className="flex items-center gap-3">
            <span className="text-[#E50914] font-black text-2xl lg:text-3xl tracking-tight">CINECINEMA</span>
            <span className="hidden sm:inline-block text-[10px] tracking-[0.2em] text-amber-200/70 border border-amber-300/30 rounded-full px-2 py-0.5 uppercase">FK Cinema</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/festival" data-testid="landing-festival-link" className="hidden md:inline text-sm text-gray-300 hover:text-white transition">Festival</Link>
            <Link to="/pricing" data-testid="landing-pricing-link" className="hidden md:inline text-sm text-gray-300 hover:text-white transition">Pricing</Link>
            <button onClick={() => nav("/login")} data-testid="landing-signin-btn" className="btn-glass px-5 py-2 rounded font-semibold text-sm">
              Sign In
            </button>
          </div>
        </header>

        {/* Hero body */}
        <div className="relative z-10 flex-1 grid lg:grid-cols-12 items-center px-6 lg:px-12 py-12 lg:py-20 gap-10 max-w-[1600px] mx-auto w-full">
          <div className="lg:col-span-7 space-y-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/15 bg-white/5 backdrop-blur text-xs uppercase tracking-widest text-gray-300">
              <span className="w-1.5 h-1.5 rounded-full bg-[#E50914] animate-pulse" />
              Streaming · Multiplex · Festival · Live TV
            </div>
            <h1 data-testid={`hero-headline-${variant}`} className="text-5xl md:text-7xl lg:text-[6.5rem] font-black tracking-[-0.03em] leading-[0.92]">
              {headline.lines[0]}<br />
              {headline.lines[1]}<br />
              {headline.lines[2]}
            </h1>
            <p className="text-lg md:text-xl text-gray-300 max-w-2xl leading-relaxed">
              Stream blockbusters at home. Catch live premieres in the GCR Multiplex. Discover indie films at the World Film Festival. Tune into 24/7 curated channels. All from one CineCinema membership.
            </p>
            <form
              onSubmit={(e) => { e.preventDefault(); onSignupClick(); }}
              className="flex flex-col sm:flex-row gap-3 max-w-2xl"
            >
              <input
                data-testid="landing-email-input"
                type="email"
                placeholder="Your email address"
                className="input-dark flex-1 text-base"
                autoComplete="email"
              />
              <button data-testid="landing-getstarted-btn" type="submit" className="btn-red px-7 py-4 rounded font-semibold text-base flex items-center justify-center gap-2 whitespace-nowrap">
                Start Watching <ArrowRight className="w-5 h-5" />
              </button>
            </form>
            <div className="flex flex-wrap items-center gap-4 pt-1">
              <button
                onClick={() => setShowTrailer(true)}
                data-testid="watch-trailer-btn"
                className="group inline-flex items-center gap-3 text-sm text-gray-300 hover:text-white transition"
              >
                <span className="w-11 h-11 rounded-full bg-white/10 group-hover:bg-white/20 border border-white/20 flex items-center justify-center transition">
                  <Play className="w-4 h-4 fill-current ml-0.5" />
                </span>
                Watch the trailer
                <span className="text-[10px] uppercase tracking-widest text-gray-500 hidden sm:inline">~30 sec</span>
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-400">
              <span className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> No ads on Plus & above</span>
              <span className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> Cancel anytime</span>
              <span className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> Plans from $7.99/mo</span>
            </div>
          </div>

          {/* Right column — featured poster stack with parallax */}
          <div className="hidden lg:flex lg:col-span-5 justify-center relative h-[520px]">
            {featured.movies?.slice(0, 4).map((m, i) => (
              <PosterCard key={m.video_id || i} movie={m} index={i} scrollY={scrollY} reducedMotion={reducedMotion} />
            ))}
          </div>
        </div>

        {/* On-Now Marquee */}
        <OnNowStrip stats={stats} />
      </section>

      {/* ============ THE FOUR PILLARS ============ */}
      <section className="px-6 lg:px-12 py-24 max-w-[1600px] mx-auto">
        <SectionHeader
          kicker="Why CineCinema"
          title="More than a streaming service."
          subtitle="Built for film lovers, filmmakers, and Friday nights alike."
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          <PillarCard
            icon={<Film />}
            title="Streaming"
            body="Thousands of titles. 4K HDR. Multi-language audio. Family profiles."
            badge={stats ? `${stats.titles}+ films` : "Library"}
            tint="red"
            to="/pricing"
            cta="Browse plans"
          />
          <PillarCard
            icon={<Clapperboard />}
            title="GCR Multiplex"
            body="Live premieres on 8 virtual screens. Early-bird tickets. In-seat snacks delivery."
            badge={`${stats?.multiplex_screens ?? 8} screens`}
            tint="gold"
            to="/screens"
            cta="See showtimes"
          />
          <PillarCard
            icon={<Award />}
            title="World Film Festival"
            body="Indie filmmakers submit. AI-curated jury. Public Laurel Wall. Revenue share."
            badge={stats?.submissions ? `${stats.submissions} entries` : "Open call"}
            tint="amber"
            to="/festival"
            cta="Explore festival"
          />
          <PillarCard
            icon={<Radio />}
            title="24/7 Live Channels"
            body="Curated channels for every mood. Tune in. Drop out. No remotes required."
            badge="Always on"
            tint="blue"
            to="/live"
            cta="What's on now"
          />
        </div>
      </section>

      {/* ============ GCR MULTIPLEX ============ */}
      <section className="relative px-6 lg:px-12 py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a0608] via-[#0A0A0A] to-[#1a0608] opacity-80" />
        <div className="absolute inset-0 opacity-30" style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 30%, rgba(229,9,20,0.25) 0%, transparent 60%)' }} />
        <div className="relative max-w-[1600px] mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 space-y-6">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-amber-300">
              <Clapperboard className="w-4 h-4" /> The GCR Multiplex
            </div>
            <h2 className="text-4xl md:text-6xl font-black tracking-tight leading-tight">
              Real cinema.<br />Without leaving the couch.
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed max-w-xl">
              Premieres on a virtual big screen — synchronized to the second across all viewers. Early-bird discounts that drop as the countdown clock ticks. Order popcorn, drinks, and combos from Zomato, Noon, or in-house kitchen delivered straight to your seat.
            </p>
            <ul className="grid grid-cols-2 gap-3 max-w-xl pt-2">
              <Bullet icon={<Sparkles />} label="Synchronized live premieres" />
              <Bullet icon={<Popcorn />} label="In-seat food & drink" />
              <Bullet icon={<Globe />} label="Geo-licensed multi-region" />
              <Bullet icon={<Crown />} label="Early-bird pricing" />
            </ul>
            <div className="flex flex-wrap gap-3 pt-4">
              <Link to="/screens" data-testid="cta-gcr-screens" className="btn-red px-6 py-3.5 rounded font-semibold inline-flex items-center gap-2">
                Browse showtimes <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/pricing" data-testid="cta-gcr-pricing" className="btn-glass px-6 py-3.5 rounded font-semibold inline-flex items-center gap-2">
                Compare plans
              </Link>
            </div>
          </div>
          {/* Visual: stacked "tickets" */}
          <div className="lg:col-span-6 relative">
            <TicketStack />
          </div>
        </div>
      </section>

      {/* ============ WORLD FILM FESTIVAL ============ */}
      <section className="px-6 lg:px-12 py-24 max-w-[1600px] mx-auto">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Festival posters grid */}
          <div className="lg:col-span-6 order-2 lg:order-1 grid grid-cols-2 gap-4">
            {(featured.festival_films?.length ? featured.festival_films : Array(4).fill(null)).slice(0, 4).map((f, i) => (
              <FestivalThumb key={f?.submission_id || i} film={f} index={i} />
            ))}
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2 space-y-6">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-amber-300">
              <Award className="w-4 h-4" /> World Film Festival
            </div>
            <h2 className="text-4xl md:text-6xl font-black tracking-tight leading-tight">
              Where new filmmakers <span className="text-amber-300">get their laurel.</span>
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed">
              Submit your short, feature, or documentary. AI-assisted pre-screening flags eligible films. A human jury awards Official Selection, Best Director, and tier laurels. Selected films get a slot on the Multiplex — and a revenue share via Stripe Connect.
            </p>
            <div className="grid grid-cols-3 gap-3 pt-2 max-w-md">
              <Stat n={stats?.submissions ?? "—"} label="Submissions" />
              <Stat n={stats?.laurels_awarded ?? "—"} label="Laurels" />
              <Stat n={stats?.festivals ?? "Open"} label="Festivals" highlightWhenZero />
            </div>
            <div className="flex flex-wrap gap-3 pt-3">
              <Link to="/festival/submit" data-testid="cta-festival-submit" className="btn-red px-6 py-3.5 rounded font-semibold inline-flex items-center gap-2">
                Submit your film <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/festival/laurels" data-testid="cta-festival-laurels" className="btn-glass px-6 py-3.5 rounded font-semibold inline-flex items-center gap-2">
                Wall of laurels
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ JURY OF THE WEEK ============ */}
      {featured.jury_of_week && (
        <section className="px-6 lg:px-12 py-20 max-w-[1400px] mx-auto">
          <div className="relative rounded-3xl overflow-hidden border border-amber-300/15 bg-gradient-to-br from-[#1a0608] via-[#100406] to-[#0A0A0A] p-8 lg:p-12">
            <div className="absolute -right-24 -top-24 w-[420px] h-[420px] rounded-full opacity-25 blur-3xl" style={{ background: "radial-gradient(circle, #f6c34a 0%, transparent 70%)" }} />
            <div className="relative grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-3 flex flex-col items-center lg:items-start">
                {(() => {
                  const j = featured.jury_of_week;
                  const p = j.photo_url ? (j.photo_url.startsWith("http") ? j.photo_url : `${process.env.REACT_APP_BACKEND_URL || ""}${j.photo_url}`) : null;
                  return p ? (
                    <img src={p} alt={j.display_name} className="w-40 h-40 rounded-full object-cover border-4 border-amber-300/60 shadow-2xl" />
                  ) : (
                    <div className="w-40 h-40 rounded-full bg-[#1a0608] border-4 border-amber-300/60 flex items-center justify-center text-5xl font-black text-[#E50914] shadow-2xl">
                      {(j.display_name || "?").charAt(0).toUpperCase()}
                    </div>
                  );
                })()}
              </div>
              <div className="lg:col-span-9 space-y-4">
                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-amber-300">
                  <Award className="w-4 h-4" /> Jury of the Week
                </div>
                <h2 data-testid="jury-of-week-name" className="text-3xl md:text-5xl font-black tracking-tight leading-tight">
                  {featured.jury_of_week.display_name}
                </h2>
                {featured.jury_of_week.bio && (
                  <p className="text-gray-300 leading-relaxed max-w-3xl">{featured.jury_of_week.bio}</p>
                )}
                {featured.jury_of_week.achievements?.length > 0 && (
                  <ul className="grid sm:grid-cols-2 gap-2 max-w-3xl pt-1">
                    {featured.jury_of_week.achievements.map((a, i) => (
                      <li key={i} className="pl-3 border-l-2 border-amber-300/50 text-sm text-gray-400 leading-relaxed">{a}</li>
                    ))}
                  </ul>
                )}
                <div className="pt-3">
                  <Link
                    data-testid="jury-of-week-cta"
                    to={`/jury/${featured.jury_of_week.slug}`}
                    className="inline-flex items-center gap-2 btn-red px-5 py-3 rounded font-semibold text-sm"
                  >
                    View full profile <ArrowRight className="w-4 h-4" />
                  </Link>
                  <span className="ml-4 text-xs text-gray-500">These are the humans deciding your film&rsquo;s fate.</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ============ PRICING ============ */}
      <section className="px-6 lg:px-12 py-24 max-w-[1400px] mx-auto">
        <SectionHeader
          kicker="Plans"
          title="Pick a tier. Cancel anytime."
          subtitle="All plans include GCR Multiplex access and the Festival."
        />
        <div className="grid md:grid-cols-3 gap-5 mt-12">
          <PricingCard
            name="Basic"
            price="7.99"
            tagline="Solo viewing on one screen at a time."
            features={["1 screen", "HD streaming", "GCR Multiplex (early-bird)", "Festival viewing", "Ad-supported"]}
          />
          <PricingCard
            featured
            name="Plus"
            price="14.99"
            tagline="The full CineCinema experience for couples and families."
            features={["4 screens", "4K HDR", "Multi-language audio", "Premiere priority queue", "Ad-free", "Offline downloads"]}
          />
          <PricingCard
            name="Premiere"
            price="24.99"
            tagline="For cinephiles. Includes filmmaker submissions."
            features={["6 screens", "4K HDR Dolby Vision", "Free festival submission", "Stripe Connect payouts", "Priority support", "Early access to new screens"]}
          />
        </div>
      </section>

      {/* ============ SNACKS ============ */}
      <section className="px-6 lg:px-12 py-24 max-w-[1600px] mx-auto">
        <div className="rounded-3xl bg-gradient-to-br from-[#1a0608] via-[#100406] to-[#0A0A0A] border border-amber-300/10 p-10 lg:p-16 grid lg:grid-cols-12 gap-10 items-center relative overflow-hidden">
          <div
            className="absolute -right-20 -top-20 w-[420px] h-[420px] rounded-full opacity-20 blur-3xl"
            style={{ background: "radial-gradient(circle, #f6c34a 0%, transparent 70%)" }}
          />
          <div className="lg:col-span-7 space-y-6 relative">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-amber-300">
              <Popcorn className="w-4 h-4" /> Cinema Snacks
            </div>
            <h2 className="text-4xl md:text-5xl font-black tracking-tight leading-tight">
              Popcorn. Coke. Mango lassi.<br /><span className="text-grad-red">Delivered to your seat.</span>
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed max-w-xl">
              Inside any GCR Multiplex screen, tap the cart. Order from Zomato, Noon, Paanidost, or our in-house cinema kitchen. Stripe checkout. Hot, sweet, salty — your show keeps playing.
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              {["Zomato", "Noon", "Paanidost", "Cinema Kitchen"].map((p) => (
                <span key={p} className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs uppercase tracking-wider text-gray-300">{p}</span>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <SnackCardStack />
          </div>
        </div>
      </section>

      {/* ============ TRUST STRIP ============ */}
      {stats && (
        <section className="px-6 lg:px-12 py-16 border-t border-white/5">
          <div className="max-w-[1400px] mx-auto grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-6 text-center">
            <BigStat n={stats.titles || "—"} label="Films & series" />
            <BigStat n={stats.submissions || "—"} label="Festival submissions" />
            <BigStat n={stats.multiplex_screens || 8} label="Multiplex screens" />
            <BigStat n={stats.laurels_awarded || "—"} label="Laurels awarded" />
          </div>
        </section>
      )}

      {/* ============ FINAL CTA ============ */}
      <section className="px-6 lg:px-12 py-24 text-center">
        <h2 className="text-4xl md:text-6xl font-black tracking-tight max-w-3xl mx-auto leading-tight">
          The cinema you carry.<br /><span className="text-grad-red">Wherever you sit.</span>
        </h2>
        <p className="text-lg text-gray-400 mt-5 max-w-xl mx-auto">Start your CineCinema membership tonight.</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
          <button onClick={onSignupClick} data-testid="cta-final-signup" className="btn-red px-8 py-4 rounded font-semibold text-lg inline-flex items-center justify-center gap-2">
            Start watching <ArrowRight className="w-5 h-5" />
          </button>
          <Link to="/pricing" data-testid="cta-final-pricing" className="btn-glass px-8 py-4 rounded font-semibold text-lg inline-flex items-center justify-center gap-2">
            See pricing
          </Link>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="border-t border-white/5 px-6 lg:px-12 py-12">
        <div className="max-w-[1600px] mx-auto grid grid-cols-2 md:grid-cols-5 gap-8 text-sm">
          <div className="col-span-2 space-y-3">
            <span className="text-[#E50914] font-black text-xl">CINECINEMA</span>
            <p className="text-gray-500 max-w-xs leading-relaxed">FK Cinema &mdash; the world&rsquo;s first all-in-one streaming, multiplex, festival and live-TV cinema.</p>
          </div>
          <FooterCol title="Watch" links={[["/browse","Browse"],["/screens","Multiplex"],["/live","Live TV"],["/festival","Festival"]]} />
          <FooterCol title="Account" links={[["/login","Sign in"],["/signup","Sign up"],["/pricing","Pricing"],["/forgot-password","Forgot password"]]} />
          <FooterCol title="Filmmakers" links={[["/festival/submit","Submit film"],["/creator/apply","Apply as creator"],["/festival/laurels","Wall of laurels"],["/creators","Creators"]]} />
        </div>
        <div className="max-w-[1600px] mx-auto mt-10 pt-6 border-t border-white/5 flex flex-col sm:flex-row justify-between gap-3 text-xs text-gray-500">
          <span>© {new Date().getFullYear()} CineCinema · FK Cinema. All rights reserved.</span>
          <span>Made with ♥ for film.</span>
        </div>
      </footer>

      {/* ============ TRAILER MODAL ============ */}
      {showTrailer && (
        <div
          data-testid="trailer-modal"
          onClick={() => setShowTrailer(false)}
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4"
        >
          <button
            data-testid="trailer-close-btn"
            onClick={(e) => { e.stopPropagation(); setShowTrailer(false); }}
            className="absolute top-6 right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center transition"
          >
            <X className="w-5 h-5" />
          </button>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-5xl">
            <div className="relative pt-[56.25%] rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-black">
              <video
                src={sizzleUrl}
                autoPlay
                controls
                className="absolute inset-0 w-full h-full"
                data-testid="trailer-video"
              />
            </div>
            {!stats?.hero_video_url && (
              <p className="text-center text-xs text-gray-500 mt-4">
                Sample reel · set <code className="text-gray-400">LANDING_HERO_VIDEO_URL</code> to swap with your own.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Building blocks                                                     */
/* ------------------------------------------------------------------ */

function SectionHeader({ kicker, title, subtitle }) {
  return (
    <div className="max-w-3xl">
      <div className="text-xs uppercase tracking-[0.25em] text-[#E50914] mb-3">{kicker}</div>
      <h2 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">{title}</h2>
      {subtitle && <p className="text-lg text-gray-400 mt-4">{subtitle}</p>}
    </div>
  );
}

function PillarCard({ icon, title, body, badge, tint = "red", to, cta }) {
  const tints = {
    red: "from-[#2a0507] to-[#0A0A0A] border-[#E50914]/30 text-[#E50914]",
    gold: "from-[#1f1605] to-[#0A0A0A] border-amber-300/30 text-amber-300",
    amber: "from-[#1a1206] to-[#0A0A0A] border-amber-200/30 text-amber-200",
    blue: "from-[#051420] to-[#0A0A0A] border-sky-400/30 text-sky-400",
  };
  return (
    <Link
      to={to || "#"}
      data-testid={`pillar-${title.toLowerCase().replace(/\s+/g, "-")}`}
      className={`group relative bg-gradient-to-br ${tints[tint]} border rounded-2xl p-7 overflow-hidden hover:-translate-y-1 transition-transform duration-300`}
    >
      <div className="flex items-start justify-between mb-6">
        <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
          {React.cloneElement(icon, { className: "w-6 h-6" })}
        </div>
        <span className="text-[10px] uppercase tracking-widest opacity-80">{badge}</span>
      </div>
      <h3 className="text-2xl font-bold text-white tracking-tight mb-2">{title}</h3>
      <p className="text-sm text-gray-400 leading-relaxed">{body}</p>
      <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold group-hover:gap-2.5 transition-all">
        {cta} <ChevronRight className="w-4 h-4" />
      </div>
    </Link>
  );
}

function OnNowStrip({ stats }) {
  const items = [
    { tag: "ON NOW", label: `Screen 1 · Premiere`, dot: true },
    { tag: "EARLY BIRD", label: `40% off · 02:14 left` },
    { tag: "FESTIVAL", label: `${stats?.submissions ?? "—"} submissions reviewed` },
    { tag: "LIVE TV", label: "Curator\u2019s Pick channel" },
  ];
  return (
    <div className="relative z-10 border-t border-white/5 bg-black/40 backdrop-blur-md">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-3 flex flex-wrap gap-x-8 gap-y-2 text-xs items-center">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-2 text-gray-300">
            {it.dot && <span className="w-1.5 h-1.5 rounded-full bg-[#E50914] animate-pulse" />}
            <span className="font-bold text-[10px] tracking-[0.18em] text-amber-300">{it.tag}</span>
            <span className="text-gray-400">{it.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PosterCard({ movie, index, scrollY = 0, reducedMotion = false }) {
  const poster = movie?.poster?.startsWith("http") ? movie.poster : `${process.env.REACT_APP_BACKEND_URL || ""}${movie?.poster || ""}`;
  const rot = [-6, -2, 3, 7][index] || 0;
  const tx = [-150, -50, 60, 150][index] || 0;
  const ty = [40, 0, 20, 80][index] || 0;
  const z = [10, 30, 20, 5][index];
  // Each card drifts at a different rate — front cards drift slowest, back cards fastest
  const parallaxFactor = [0.15, 0.05, 0.10, 0.22][index] || 0;
  const parallaxOffset = reducedMotion ? 0 : -scrollY * parallaxFactor;
  return (
    <div
      data-testid={`hero-poster-${index}`}
      className="absolute top-1/2 left-1/2 w-[220px] h-[330px] rounded-xl overflow-hidden shadow-2xl border border-white/10 hover:scale-105 hover:rotate-0 transition-transform duration-500 cursor-pointer will-change-transform"
      style={{
        transform: `translate(calc(-50% + ${tx}px), calc(-50% + ${ty + parallaxOffset}px)) rotate(${rot}deg)`,
        zIndex: z,
      }}
    >
      {movie?.poster ? (
        <img src={poster} alt={movie.title} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-[#2a0507] to-[#0A0A0A] flex items-center justify-center">
          <Film className="w-12 h-12 text-white/20" />
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/60 to-transparent p-3">
        <p className="text-xs font-bold truncate">{movie?.title || "Coming soon"}</p>
        <p className="text-[10px] text-gray-400 mt-0.5">{movie?.year} · {movie?.type === "series" ? "Series" : "Film"}</p>
      </div>
    </div>
  );
}

function Bullet({ icon, label }) {
  return (
    <li className="flex items-center gap-2.5 text-sm text-gray-300">
      <span className="w-7 h-7 rounded-full bg-amber-300/10 border border-amber-300/30 flex items-center justify-center text-amber-300">
        {React.cloneElement(icon, { className: "w-3.5 h-3.5" })}
      </span>
      {label}
    </li>
  );
}

function TicketStack() {
  return (
    <div className="relative h-[420px] flex items-center justify-center">
      {[2, 1, 0].map((i) => (
        <div
          key={i}
          className="absolute w-[300px] md:w-[360px] h-[180px] rounded-2xl bg-gradient-to-br from-[#1a0608] to-[#2a0a0e] border border-amber-200/20 shadow-2xl p-6 flex flex-col justify-between"
          style={{
            transform: `translateY(${i * -18}px) translateX(${i * 24}px) rotate(${i * -3}deg)`,
            zIndex: 10 - i,
            opacity: 1 - i * 0.2,
          }}
        >
          <div className="flex justify-between items-start">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-amber-300">Screen {i + 1}</p>
              <p className="text-lg font-bold mt-1 truncate">{["The Premiere", "Indie Night", "Festival Special"][i]}</p>
            </div>
            <Clapperboard className="w-5 h-5 text-amber-300/70" />
          </div>
          <div className="flex justify-between items-end">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-gray-500">Showtime</p>
              <p className="text-sm font-semibold">{["8:00 PM", "9:30 PM", "11:00 PM"][i]}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wider text-gray-500">Price</p>
              <p className="text-sm font-bold text-[#E50914]">${["3.99", "4.99", "5.99"][i]}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function FestivalThumb({ film, index }) {
  const poster = film?.tier_poster_url || film?.poster_url;
  const fullPoster = poster?.startsWith("http") ? poster : `${process.env.REACT_APP_BACKEND_URL || ""}${poster || ""}`;
  return (
    <div className="aspect-[2/3] rounded-xl overflow-hidden border border-amber-300/20 relative group">
      {poster ? (
        <img src={fullPoster} alt={film.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-[#1f1605] to-[#0A0A0A] flex items-center justify-center">
          <Award className="w-12 h-12 text-amber-300/30" />
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/70 to-transparent p-3">
        <p className="text-xs font-bold truncate">{film?.title || `Coming soon ${index + 1}`}</p>
        <p className="text-[10px] text-amber-300 mt-0.5 truncate">{film?.filmmaker_name || "TBA"}</p>
      </div>
    </div>
  );
}

function Stat({ n, label, highlightWhenZero }) {
  const isZero = n === 0 || n === "0";
  return (
    <div>
      <div className={`text-3xl font-black ${isZero && highlightWhenZero ? "text-amber-300" : "text-white"}`}>{n}</div>
      <div className="text-[11px] uppercase tracking-widest text-gray-500 mt-1">{label}</div>
    </div>
  );
}

function BigStat({ n, label }) {
  return (
    <div>
      <div className="text-4xl md:text-6xl font-black text-grad-red">{n}</div>
      <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mt-2">{label}</div>
    </div>
  );
}

function PricingCard({ name, price, tagline, features, featured }) {
  return (
    <div
      data-testid={`pricing-card-${name.toLowerCase()}`}
      className={`rounded-2xl p-7 flex flex-col gap-5 ${featured
        ? "bg-gradient-to-br from-[#2a0507] via-[#1a0608] to-[#0A0A0A] border-2 border-[#E50914] shadow-2xl shadow-[#E50914]/20 scale-[1.02]"
        : "bg-white/[0.02] border border-white/10"}`}
    >
      <div className="flex items-baseline justify-between">
        <h3 className="text-2xl font-bold">{name}</h3>
        {featured && <span className="text-[10px] uppercase tracking-widest bg-[#E50914] text-white rounded-full px-2 py-0.5">Most popular</span>}
      </div>
      <div>
        <span className="text-5xl font-black tracking-tight">${price}</span>
        <span className="text-gray-500 text-sm">/mo</span>
      </div>
      <p className="text-sm text-gray-400 -mt-2">{tagline}</p>
      <ul className="space-y-2.5 text-sm text-gray-300 flex-1">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2.5">
            <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> {f}
          </li>
        ))}
      </ul>
      <Link to="/signup" className={featured ? "btn-red text-center px-5 py-3 rounded font-semibold" : "btn-glass text-center px-5 py-3 rounded font-semibold"}>
        Start with {name}
      </Link>
    </div>
  );
}

function SnackCardStack() {
  const items = [
    { name: "Salted Popcorn", price: "4.99", color: "from-amber-500 to-yellow-300" },
    { name: "Coke Zero", price: "2.99", color: "from-red-700 to-red-500" },
    { name: "Mango Smoothie", price: "5.99", color: "from-orange-500 to-amber-300" },
  ];
  return (
    <div className="relative h-[340px] flex items-center justify-center">
      {items.map((it, i) => (
        <div
          key={it.name}
          className="absolute w-[240px] h-[120px] rounded-2xl border border-white/10 bg-gradient-to-br shadow-2xl p-5 flex flex-col justify-between text-black"
          style={{
            transform: `translate(${(i - 1) * 50}px, ${i * -36}px) rotate(${(i - 1) * 6}deg)`,
            zIndex: 10 - i,
          }}
        >
          <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${it.color} opacity-90`} />
          <div className="relative flex justify-between">
            <Popcorn className="w-6 h-6 text-black/70" />
            <span className="text-[10px] uppercase tracking-wider font-bold text-black/70">In-seat</span>
          </div>
          <div className="relative">
            <p className="font-bold">{it.name}</p>
            <p className="text-2xl font-black mt-0.5">${it.price}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <h4 className="text-white font-semibold mb-3 text-sm">{title}</h4>
      <ul className="space-y-2 text-gray-500">
        {links.map(([href, label]) => (
          <li key={href}><Link to={href} className="hover:text-white transition">{label}</Link></li>
        ))}
      </ul>
    </div>
  );
}
